import { useTranslation } from 'react-i18next';

export type EmporterFilterKey = 'all' | 'counter' | 'online';

interface Props {
  counterCount: number;
  onlineCount: number;
  active: EmporterFilterKey;
  onSelect: (key: EmporterFilterKey) => void;
}

const KEYS: EmporterFilterKey[] = ['all', 'counter', 'online'];

const LABEL_KEY: Record<EmporterFilterKey, string> = {
  all: 'waiter.notes.online.filterAll',
  counter: 'waiter.notes.online.filterCounter',
  online: 'waiter.notes.online.filterOnline',
};

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.3 D5 — the Emporter tab's own
 * filter row: `Tout` · `Comptoir n` · `En ligne n`, default `Tout`. Same chip
 * shape as `TableStateFilter` (Sur place's D2 legend). Unlike that filter,
 * there is no re-tap-to-clear here — `all` already means "show everything",
 * so every tap is a plain selection.
 */
export default function EmporterSourceFilter({ counterCount, onlineCount, active, onSelect }: Props) {
  const { t } = useTranslation();
  const counts: Record<EmporterFilterKey, number> = {
    all: counterCount + onlineCount,
    counter: counterCount,
    online: onlineCount,
  };

  return (
    <div
      data-testid="emporter-filter"
      role="group"
      aria-label={t('waiter.notes.tabs.emporter')}
      className="flex items-center gap-2 overflow-x-auto px-3 py-2 bg-paper border-b border-hairline"
    >
      {KEYS.map((key) => (
        <button
          key={key}
          type="button"
          data-testid={`emporter-chip-${key}`}
          aria-pressed={active === key}
          onClick={() => onSelect(key)}
          className={`shrink-0 h-11 px-2.5 flex items-center gap-1.5 rounded-full border-2 font-sans text-sm font-semibold ${
            active === key ? 'border-ink bg-ink text-paper' : 'border-transparent text-ink'
          }`}
        >
          {t(LABEL_KEY[key])} {counts[key]}
        </button>
      ))}
    </div>
  );
}
